import { Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { classRoomService } from './api/class-room.service';

/**
 * Premium gate for AI Virtual Assistant from `ai_virtual_assistant` on
 * `get-class-by-teacher-id`. Enabled only when value is strictly `true`.
 */
@Injectable({
  providedIn: 'root',
})
export class AiAssistantAccessService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly classRoom = inject(classRoomService);

  /** `true` only when API returned `ai_virtual_assistant === true`. */
  readonly isAiAssistantEnabled = signal(false);

  /** After first apply or failed load attempt (avoids duplicate fetches). */
  readonly accessHydrated = signal(false);

  readonly premiumModalOpen = signal(false);

  showPremiumModal(): void {
    this.premiumModalOpen.set(true);
  }

  closePremiumModal(): void {
    this.premiumModalOpen.set(false);
  }

  /**
   * Normalize API field: only boolean `true` enables; strings "true"/"1" for backend quirks.
   */
  private static parseAiVirtualAssistantFlag(value: unknown): boolean {
    if (value === true) return true;
    if (value === false || value == null) return false;
    if (typeof value === 'string') {
      const s = value.trim().toLowerCase();
      return s === 'true' || s === '1';
    }
    if (typeof value === 'number') return value === 1;
    return false;
  }

  applyFromClassByTeacherPayload(data: Record<string, unknown>): void {
    const enabled = AiAssistantAccessService.parseAiVirtualAssistantFlag(
      data['ai_virtual_assistant']
    );
    this.isAiAssistantEnabled.set(enabled);
    this.accessHydrated.set(true);
  }

  /**
   * Loads teacher class payload once (FAB / route guard) when not yet hydrated.
   */
  ensureAccessLoaded(): Observable<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return of(undefined);
    }
    if (this.accessHydrated()) {
      return of(undefined);
    }
    const token =
      typeof localStorage !== 'undefined'
        ? localStorage.getItem('token')
        : null;
    if (!token) {
      this.isAiAssistantEnabled.set(false);
      this.accessHydrated.set(true);
      return of(undefined);
    }
    let teacherId: number | undefined;
    try {
      const raw = localStorage.getItem('user');
      if (!raw) {
        this.isAiAssistantEnabled.set(false);
        this.accessHydrated.set(true);
        return of(undefined);
      }
      const u = JSON.parse(raw) as { teacher_id?: number };
      teacherId = u.teacher_id != null ? Number(u.teacher_id) : undefined;
    } catch {
      this.isAiAssistantEnabled.set(false);
      this.accessHydrated.set(true);
      return of(undefined);
    }
    if (teacherId == null || !Number.isFinite(teacherId)) {
      this.isAiAssistantEnabled.set(false);
      this.accessHydrated.set(true);
      return of(undefined);
    }

    return this.classRoom.getClassRoomByTeacherID(teacherId).pipe(
      tap(res => {
        const data =
          (res as { data?: Record<string, unknown> })?.data ??
          (res as Record<string, unknown>) ??
          {};
        this.applyFromClassByTeacherPayload(data as Record<string, unknown>);
      }),
      map(() => undefined),
      catchError(() => {
        this.isAiAssistantEnabled.set(false);
        this.accessHydrated.set(true);
        return of(undefined);
      })
    );
  }
}
